// src/features/ask/components/AssistantPanel.tsx
//
// «Preguntar» as a side panel, opened from the app bar: docked on the right on
// a wide screen (the layout makes room for it, so the screen and the
// conversation are both visible — the arrangement of Copilot in Power BI and
// Gemini in Workspace), a full-height drawer over the screen below that. The
// conversation lives in AssistantProvider, so it survives navigation; the
// newest turn is at the bottom, next to the question box.
import React from 'react';
import { Box, Chip, Divider, Drawer, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AddCommentOutlined from '@mui/icons-material/AddCommentOutlined';
import { useTranslate } from 'react-admin';
import { APP_BAR_HEIGHT } from '@/layouts/useSectionTabs';
import { useAssistant } from '../AssistantProvider';
import { AskComposer } from './AskComposer';
import { AskTurnCard } from './AskTurnCard';

export const ASSISTANT_PANEL_WIDTH = 400;

const SUGGESTION_KEYS = ['selfService', 'worstPreCheckin', 'evolution', 'kioskTime', 'charged', 'frontDesk'] as const;

/** Docked beside the screen from this breakpoint up; a drawer over it below. */
export const useAssistantDocked = (): boolean => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up('lg'));
};

const PanelContent: React.FC = () => {
  const translate = useTranslate();
  const { turns, busy, hotelsInScope, send, undo, undoableTurnId, startOver, setOpen, view } = useAssistant();
  const [question, setQuestion] = React.useState('');
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Bring the newest turn's question to the top, so its answer is read from the start.
  React.useEffect(() => {
    const articles = listRef.current?.querySelectorAll('article');
    articles?.[articles.length - 1]?.scrollIntoView({ block: 'start' });
  }, [turns]);

  const submit = (text: string) => {
    if (busy) return;
    send(text);
    setQuestion('');
  };

  const suggestions = [
    ...(view && view.family !== 'none' && view.hasPeriod ? [translate('resources.ask.suggestions.filterScreen')] : []),
    ...SUGGESTION_KEYS.map((key) => translate(`resources.ask.suggestions.${key}`)),
  ];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <Box
        sx={{
          height: APP_BAR_HEIGHT,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, flex: 1 }}>
          {translate('resources.ask.panel.title')}
        </Typography>
        {turns.length > 0 && (
          <Tooltip title={translate('resources.ask.composer.newConversation')}>
            <span>
              <IconButton size="small" onClick={startOver} disabled={busy} aria-label={translate('resources.ask.composer.newConversation')}>
                <AddCommentOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={translate('resources.ask.panel.close')}>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label={translate('resources.ask.panel.close')}>
            <CloseRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box ref={listRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
        {turns.length === 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('resources.ask.panel.intro')}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
              {translate('resources.ask.suggestions.title')}
            </Typography>
            <Stack spacing={1} alignItems="flex-start">
              {suggestions.map((text) => (
                <Chip
                  key={text}
                  label={text}
                  variant="outlined"
                  onClick={() => submit(text)}
                  sx={{ maxWidth: '100%', height: 'auto', py: 0.75, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Stack spacing={3} divider={<Divider flexItem />}>
            {turns.map((turn) => (
              <AskTurnCard
                key={turn.id}
                turn={turn}
                onRetry={(failed) => send(failed.question, failed.id)}
                onUndo={undo}
                canUndo={turn.id === undoableTurnId}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={{ flex: 'none', px: 2, pt: 1.5, pb: 2, borderTop: 1, borderColor: 'divider' }}>
        {view && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }} noWrap>
            {translate('resources.ask.panel.lookingAt', { screen: view.screenLabel })}
          </Typography>
        )}
        <AskComposer
          value={question}
          onChange={setQuestion}
          onSubmit={() => submit(question)}
          busy={busy}
          hotelsInScope={hotelsInScope}
          inputRef={inputRef}
        />
      </Box>
    </Box>
  );
};

export const AssistantPanel: React.FC = () => {
  const { open, setOpen } = useAssistant();
  const docked = useAssistantDocked();
  const translate = useTranslate();

  if (docked) {
    if (!open) return null;
    return (
      <Box
        component="aside"
        aria-label={translate('resources.ask.panel.title')}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: ASSISTANT_PANEL_WIDTH,
          height: '100vh',
          bgcolor: 'background.paper',
          borderLeft: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <PanelContent />
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{ sx: { width: { xs: '100%', sm: ASSISTANT_PANEL_WIDTH }, height: '100dvh' } }}
    >
      <PanelContent />
    </Drawer>
  );
};

export default AssistantPanel;
